import { UserCheck } from 'lucide-react'

import type { Contact } from '@/gen/cortado/v1/contacts_pb'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { formatFullName } from '@/lib/utils'

interface DuplicateContactAlertProps {
  contact: Contact
  onUseExisting: (contactId: string) => void
  disabled?: boolean
}

/**
 * Alert component shown when a contact with the entered email already exists.
 * Displays the existing contact's name and provides a button to use them instead.
 */
export function DuplicateContactAlert({
  contact,
  onUseExisting,
  disabled = false,
}: DuplicateContactAlertProps) {
  const displayName = formatFullName(contact.firstName, contact.lastName, contact.email || contact.phoneNumber || 'Unknown contact')

  return (
    <Alert className="bg-status-warning-bg border-status-warning-border">
      <UserCheck className="h-4 w-4 text-status-warning-fg" />
      <AlertTitle className="text-status-warning-fg">
        Contact already exists
      </AlertTitle>
      <AlertDescription className="flex flex-col gap-2">
        <span>
          A contact with this email already exists: <strong>{displayName}</strong>
          {contact.email && <span className="text-muted-foreground"> ({contact.email})</span>}
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-fit"
          onClick={() => onUseExisting(contact.id)}
          disabled={disabled}
        >
          Use existing contact
        </Button>
      </AlertDescription>
    </Alert>
  )
}
